<script>
import { defineComponent, h as createEl } from 'vue'

export default defineComponent({
  name: 'ObstacleShape',
  props: {
    obs: { type: Object, required: true },   // { type, dir?, ... }
    px:  { type: Number, required: true },   // state.scale
    spec:{ type: Object, required: true },   // { wm, hm, thicknesses?, gaps?, barThickness?, spreadM?, dir? }
    viewScale: { type: Number, default: 1 }, // state.view?.scale
  },
  setup(props) {
    return () => {
      const o        = props.obs
      const spec     = props.spec
      const px       = props.px
      const s        = props.viewScale || 1
      const sInv     = 1 / s

      const ARROW_HEAD_PX = 12;   // comprimento fixo da ponta (px)
      const ARROW_HEAD_W  = 8;    // largura da ponta (px)
      const ARROW_STROKE  = 2;    // espessura do traço (px)
      const ARROW_MARGIN  = 4;    // folga mínima (px) entre ponta e borda

      // Largura em px
      const w = Math.round((spec.wm ?? 3) * px)

      // Listas (em METROS)
      let thM = (Array.isArray(spec.thicknesses) && spec.thicknesses.length)
        ? spec.thicknesses.slice()
        : [spec.barThickness ?? 0.10]
      let gpM = Array.isArray(spec.gaps) ? spec.gaps.slice() : []

      // Alinhar com direção visual (dir=1 => entrada por baixo)
      if (spec.dir === 1) { thM.reverse(); gpM.reverse() }

      // Altura do “pacote” (m) e altura do SVG (px)
      const depthM = thM.reduce((a,b)=>a+b,0) + gpM.reduce((a,b)=>a+b,0)
      const h  = Math.round(Math.max(spec.hm ?? 0.5, depthM) * px)
      const cx = Math.round(h / 2) // centro Y

      // Conversão para px
      const bhList  = thM.map(t => Math.max(2, Math.round(t * px)))
      const gapList = gpM.map(g => Math.max(2, Math.round(g * px)))

      // Cursor inicial centralizado
      const totalH  = bhList.reduce((a,b)=>a+b,0) + gapList.reduce((a,b)=>a+b,0)
      let   yCursor = Math.round(cx - totalH/2)

      // ===== START / FINISH =====
      if (o.type === 'start' || o.type === 'finish') {
        const mastH = Math.max(18, Math.round(h * 0.6))
        const mastW = 2
        const barH  = Math.max(4, Math.round((thM[0] ?? 0.10) * px))

        const lfX  = 2
        const rfX  = w - 2
        const topY = 2
        const barY = mastH + 4

        const children = []

        // plank de chão
        children.push(createEl('rect', {
          x: 0, y: barY, width: w, height: barH,
          fill: o.type === 'start' ? '#3b82f6' : '#111',
          stroke: '#111', 'stroke-width': 0.6
        }))

        // hastes
        children.push(createEl('rect', { x: lfX, y: topY, width: mastW, height: mastH, fill: '#111', 'vector-effect': 'non-scaling-stroke' }))
        children.push(createEl('rect', { x: rfX - mastW, y: topY, width: mastW, height: mastH, fill: '#111', 'vector-effect': 'non-scaling-stroke' }))

        // seta de direção (perpendicular à barra)
        const centerX = w / 2
        const centerY = h * 0.15
        // haste cresce com h, mas não ultrapassa a largura
        const maxLenByH = Math.max(0, h - ARROW_HEAD_PX - ARROW_MARGIN*2)
        const maxLenByW = Math.max(0, w * 0.8 - ARROW_HEAD_PX)
        const shaftLen  = Math.max(8, Math.min(maxLenByH, maxLenByW))

        const shaftStartX = centerX - (shaftLen + ARROW_HEAD_PX)/2
        const shaftEndX   = centerX + (shaftLen + ARROW_HEAD_PX)/2 - ARROW_HEAD_PX

        const shaftPath = `M ${shaftStartX} ${centerY} L ${shaftEndX} ${centerY}`
        const tipPath   = `M ${shaftEndX} ${centerY - ARROW_HEAD_W/2}
                          L ${shaftEndX + ARROW_HEAD_PX} ${centerY}
                          L ${shaftEndX} ${centerY + ARROW_HEAD_W/2} Z`
        const perpDeg   = (spec.dir === -1 ? 90 : -90)

        children.push(createEl('g', { transform: `rotate(${perpDeg}, ${centerX}, ${centerY})`, style: 'pointer-events:none' }, [
          createEl('path', { d: shaftPath, stroke: '#d00', 'stroke-width': ARROW_STROKE, fill: 'none', 'stroke-linecap': 'round' }),
          createEl('path', { d: tipPath,   fill: '#d00', stroke: '#d00', 'stroke-width': 1 })
        ]))

        // bandeiras + texto “Start/Finish” fixos (não escalam com zoom)
        const fixed = []
        const flagW = 10, flagH = 7, off = 2
        fixed.push(createEl('path', {
          d: `M ${lfX+mastW} ${topY+off} l ${flagW} ${flagH/2} l ${-flagW} ${flagH/2} Z`,
          fill: '#9a5528', stroke: '#6b3b1a', 'stroke-width': 0.8
        }))
        fixed.push(createEl('path', {
          d: `M ${rfX-mastW} ${topY+off} l ${-flagW} ${flagH/2} l ${flagW} ${flagH/2} Z`,
          fill: '#9a5528', stroke: '#6b3b1a', 'stroke-width': 0.8
        }))
        const fontPx = 12
        fixed.push(createEl('text', {
          x: w/2, y: Math.max(fontPx+2, topY + mastH*0.15),
          'text-anchor': 'middle', 'font-size': fontPx, 'font-family': 'Inter, system-ui, sans-serif',
          fill: '#111', stroke: '#fff', 'stroke-width': 3, 'paint-order': 'stroke', 'letter-spacing': 0.3
        }, o.type === 'start' ? 'Start' : 'Finish'))

        // grupo que “desescala” (fixo na tela)
        children.push(createEl('g', { transform: `scale(${sInv})`, style: 'pointer-events:none' }, fixed))

        return createEl('svg', { width: w, height: h, class: 'jump map', style: 'overflow:visible' }, children)
      }

      // ===== DEMAIS TIPOS =====
      const children = []
      for (let i = 0; i < bhList.length; i++) {
        const bh = bhList[i]

        // vertical/oxer/tripleBar → padrão PB listrado (zebra ao longo do comprimento)
        const isStriped = (o.type === 'vertical' || o.type === 'oxer' || o.type === 'tripleBar')
        if (isStriped) {
          const segs = []
          const segCount = 5
          const segW = w / segCount
          for (let s = 0; s < segCount; s++) {
            const x0 = Math.round(s * segW)
            const x1 = Math.round((s + 1) * segW)
            const fill = (s % 2 === 0) ? '#000' : '#fff'
            segs.push(createEl('rect', { x: x0, y: yCursor, width: x1 - x0, height: bh, fill, class: 'bar-seg' }))
          }
          // contorno fino por cima
          segs.push(createEl('rect', { x: 0, y: yCursor, width: w, height: bh, fill: 'none', stroke: '#000', 'stroke-width': 0.5 }))
          children.push(createEl('g', null, segs))
        } else {
          // gate / plank / wall (muro) → sólidos
          const fill =
            o.type === 'gate'  ? '#444' :
            o.type === 'plank' ? '#2563eb' :
            /* wall default */   '#8B5E3C'
          children.push(createEl('rect', { x: 0, y: yCursor, width: w, height: bh, fill, stroke: '#000', 'stroke-width': 0.8 }))
        }

        yCursor += bh
        if (i < gapList.length) yCursor += gapList[i]
      }

      // Seta de direção (perpendicular à pilha), como no original
      const centerX = w / 2
      const centerY = h / 2   // ✅ centralizada (antes estava h * 0.15)

      const maxLenByH = Math.max(0, h - ARROW_HEAD_PX - ARROW_MARGIN*2)
      const maxLenByW = Math.max(0, w * 0.8 - ARROW_HEAD_PX)
      const shaftLen  = Math.max(8, Math.min(maxLenByH, maxLenByW))

      const shaftStartX = centerX - (shaftLen + ARROW_HEAD_PX)/2
      const shaftEndX   = centerX + (shaftLen + ARROW_HEAD_PX)/2 - ARROW_HEAD_PX

      const shaftPath = `M ${shaftStartX} ${centerY} L ${shaftEndX} ${centerY}`
      const tipPath   = `M ${shaftEndX} ${centerY - ARROW_HEAD_W/2}
                        L ${shaftEndX + ARROW_HEAD_PX} ${centerY}
                        L ${shaftEndX} ${centerY + ARROW_HEAD_W/2} Z`
      const perpDeg   = (spec.dir === -1 ? 90 : -90)

      children.push(createEl('g', {
        transform: `rotate(${perpDeg}, ${centerX}, ${centerY})`,
        style: 'pointer-events:none'
      }, [
        createEl('path', { d: shaftPath, stroke: '#d00', 'stroke-width': ARROW_STROKE, fill: 'none', 'stroke-linecap': 'round' }),
        createEl('path', { d: tipPath,   fill: '#d00', stroke: '#d00', 'stroke-width': 1 })
      ]))

      return createEl('svg', { width: w, height: h, class: 'jump map', style: 'overflow:visible' }, children)
    }
  }
})
</script>
